import { isArabicLanguage, toArabicIndicDigitsInString } from "src/utils/numberFormat";

export interface GoogleAdsCampaignBudget {
  campaignId: string;
  campaignName: string;
  conversions: number;
  revenue: number;
  purchases: number;
  costMicros: number;
  conversionsValue: number;
}

export interface GoogleAdsCampaignConversion {
  campaignId: string;
  campaignName: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  costMicros: number;
  revenue: number;
  purchases: number;
  campaignStartDate: string;
  campaignEndDate: string;
  date: string;
}

export interface GoogleAdsAdLevelData {
  adGroupId: string;
  adGroupName: string;
  adId: string;
  adName: string;
  adType: string;
  campaignId: string;
  campaignName: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  costMicros: number;
  revenue: number;
  purchases: number;
  engagementRate: number;
  date: string;
}

export interface GoogleAdsAggregatedMetrics {
  totalSpend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  purchases: number;
  conversionsValue: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
}

export interface GoogleAdsDailyMetric {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
}

const campaignNames = [
  "Brand Awareness - Search",
  "Product Launch - Display",
  "Retargeting - Shopping",
  "Lead Gen - Performance Max",
  "Holiday Sale - Video",
] as const;

const adGroupNames = [
  "Branded Keywords",
  "Competitor Keywords",
  "Generic Search",
  "Display Remarketing",
  "Shopping Feed",
  "YouTube In-Stream",
  "Discovery Ads",
  "Performance Max - Signals",
] as const;

const adNames = [
  "RSA - Brand Terms v1",
  "RSA - Product Features v2",
  "Display - Banner 300x250",
  "Display - Leaderboard 728x90",
  "Shopping - Product Feed A",
  "Shopping - Product Feed B",
  "Video - 15s Bumper",
  "Video - 30s TrueView",
  "Discovery - Lifestyle Image",
  "PMax - Auto Assets",
] as const;

const adTypes = [
  "SEARCH",
  "SEARCH_PARTNERS",
  "DISCOVER",
  "GMAIL",
  "YOUTUBE",
  "CONTENT",
] as const;

const generateDates = (days: number) => {
  const dates: string[] = [];
  const anchorDate = new Date("2025-12-31T00:00:00");

  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(anchorDate);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split("T")[0]);
  }

  return dates;
};

const hashValue = (...parts: Array<string | number>) =>
  parts.join("|").split("").reduce((total, char, index) => {
    return (total * 33 + char.charCodeAt(0) + index) % 1000003;
  }, 5381);

const seededNumber = (
  min: number,
  max: number,
  ...parts: Array<string | number>
) => {
  const value = hashValue(...parts);
  return min + (value % (max - min + 1));
};

const DATES = generateDates(30);

export const microsToCurrency = (micros: number) => micros / 1_000_000;

/** Compact numeric formatting without a hardcoded currency (use paid media KPI currency when displaying money). */
export const formatCurrency = (amount: number) => {
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(2)}M`;
  }

  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(1)}K`;
  }

  return amount.toFixed(2);
};

const numberLocaleForLanguage = (language: string) =>
  isArabicLanguage(language) ? "ar-EG-u-nu-arab" : "en-US";

export const formatNumber = (value: number, language = "en") => {
  const loc = numberLocaleForLanguage(language);
  if (value >= 1_000_000) {
    const s = `${(value / 1_000_000).toFixed(2)}M`;
    return isArabicLanguage(language) ? toArabicIndicDigitsInString(s) : s;
  }

  if (value >= 1_000) {
    const s = `${(value / 1_000).toFixed(1)}K`;
    return isArabicLanguage(language) ? toArabicIndicDigitsInString(s) : s;
  }

  return value.toLocaleString(loc);
};

export const formatPercent = (value: number, language = "en") => {
  const s = `${value.toFixed(2)}%`;
  return isArabicLanguage(language) ? toArabicIndicDigitsInString(s) : s;
};

export const calculateCtr = (clicks: number, impressions: number) =>
  impressions > 0 ? (clicks / impressions) * 100 : 0;

export const calculateCpc = (spend: number, clicks: number) =>
  clicks > 0 ? spend / clicks : 0;

export const calculateCpa = (spend: number, conversions: number) =>
  conversions > 0 ? spend / conversions : 0;

export const calculateRoas = (revenue: number, spend: number) =>
  spend > 0 ? revenue / spend : 0;

export const GOOGLE_ADS_CAMPAIGN_BUDGETS: GoogleAdsCampaignBudget[] =
  campaignNames.map((campaignName, index) => ({
    campaignId: `camp_${index + 1}`,
    campaignName,
    conversions: seededNumber(50, 500, campaignName, "budget", "conversions"),
    revenue: seededNumber(200000, 2000000, campaignName, "budget", "revenue"),
    purchases: seededNumber(30, 300, campaignName, "budget", "purchases"),
    costMicros: seededNumber(
      50_000_000,
      500_000_000,
      campaignName,
      "budget",
      "cost"
    ),
    conversionsValue: seededNumber(
      150000,
      1500000,
      campaignName,
      "budget",
      "value"
    ),
  }));

export const GOOGLE_ADS_CAMPAIGN_CONVERSIONS: GoogleAdsCampaignConversion[] =
  DATES.flatMap(date =>
    campaignNames.map((campaignName, index) => {
      const impressions = seededNumber(5000, 50000, campaignName, date, "impressions");
      const clicks = seededNumber(
        100,
        Math.max(100, Math.floor(impressions * 0.05)),
        campaignName,
        date,
        "clicks"
      );
      const conversions = seededNumber(
        1,
        Math.max(1, Math.floor(clicks * 0.1)),
        campaignName,
        date,
        "conversions"
      );
      const costMicros = seededNumber(
        5_000_000,
        50_000_000,
        campaignName,
        date,
        "cost"
      );

      return {
        campaignId: `camp_${index + 1}`,
        campaignName,
        impressions,
        clicks,
        ctr: calculateCtr(clicks, impressions),
        conversions,
        costMicros,
        revenue: seededNumber(10000, 200000, campaignName, date, "revenue"),
        purchases: seededNumber(
          1,
          Math.max(1, conversions),
          campaignName,
          date,
          "purchases"
        ),
        campaignStartDate: "2025-01-01",
        campaignEndDate: "2025-12-31",
        date,
      };
    })
  );

export const GOOGLE_ADS_AD_LEVEL_DATA: GoogleAdsAdLevelData[] = DATES.flatMap(date =>
  adNames.map((adName, index) => {
    const impressions = seededNumber(1000, 20000, adName, date, "impressions");
    const clicks = seededNumber(
      20,
      Math.max(20, Math.floor(impressions * 0.04)),
      adName,
      date,
      "clicks"
    );
    const conversions = seededNumber(
      0,
      Math.max(0, Math.floor(clicks * 0.08)),
      adName,
      date,
      "conversions"
    );
    const costMicros = seededNumber(1_000_000, 20_000_000, adName, date, "cost");

    return {
      adGroupId: `ag_${(index % adGroupNames.length) + 1}`,
      adGroupName: adGroupNames[index % adGroupNames.length],
      adId: `ad_${index + 1}`,
      adName,
      adType: adTypes[index % adTypes.length],
      campaignId: `camp_${(index % campaignNames.length) + 1}`,
      campaignName: campaignNames[index % campaignNames.length],
      impressions,
      clicks,
      ctr: calculateCtr(clicks, impressions),
      conversions,
      costMicros,
      revenue: seededNumber(5000, 100000, adName, date, "revenue"),
      purchases: seededNumber(
        0,
        Math.max(0, conversions),
        adName,
        date,
        "purchases"
      ),
      engagementRate: seededNumber(10, 500, adName, date, "engagement") / 100,
      date,
    };
  })
);

export const aggregateCampaignConversions = (
  rows: GoogleAdsCampaignConversion[]
): GoogleAdsAggregatedMetrics => {
  const totalSpend = rows.reduce((sum, row) => sum + microsToCurrency(row.costMicros), 0);
  const impressions = rows.reduce((sum, row) => sum + row.impressions, 0);
  const clicks = rows.reduce((sum, row) => sum + row.clicks, 0);
  const conversions = rows.reduce((sum, row) => sum + row.conversions, 0);
  const revenue = rows.reduce((sum, row) => sum + row.revenue, 0);
  const purchases = rows.reduce((sum, row) => sum + row.purchases, 0);

  return {
    totalSpend,
    impressions,
    clicks,
    conversions,
    revenue,
    purchases,
    conversionsValue: revenue,
    ctr: calculateCtr(clicks, impressions),
    cpc: calculateCpc(totalSpend, clicks),
    cpa: calculateCpa(totalSpend, conversions),
    roas: calculateRoas(revenue, totalSpend),
  };
};

export const getDailyMetrics = (rows: GoogleAdsCampaignConversion[]) => {
  const metricsByDate = new Map<string, GoogleAdsDailyMetric>();

  rows.forEach(row => {
    const metric = metricsByDate.get(row.date) ?? {
      date: row.date,
      spend: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      revenue: 0,
    };

    metric.spend += microsToCurrency(row.costMicros);
    metric.impressions += row.impressions;
    metric.clicks += row.clicks;
    metric.conversions += row.conversions;
    metric.revenue += row.revenue;

    metricsByDate.set(row.date, metric);
  });

  return Array.from(metricsByDate.values()).sort((left, right) =>
    left.date.localeCompare(right.date)
  );
};
